import type { DoozMove, DoozView } from "@bing/engine-dooz";
import { BOT_DIFFICULTIES, type BotDifficulty, type DoozMatchLength } from "@bing/shared";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DifficultySelect } from "../components/DifficultySelect";
import { MatchEndCard, namedMember } from "../components/MatchEndCard";
import { faDigits } from "../i18n/digits";
import type { SeatIdentity } from "./seat-identity";
import { StartRow, StartScreenShell } from "../components/StartScreenShell";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import {
  DoozColorContext,
  DoozColorSelect,
  DoozHandsSelect,
  DoozTable,
} from "../screens/DoozTable";
import {
  getDoozColor,
  getDoozDifficulty,
  getDoozHands,
  setDoozColor,
  setDoozDifficulty,
  setDoozHands,
} from "../settings/prefs";
import { useSession } from "../store/context";
import type { DoozPieceColor } from "./dooz-colors";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { takeResumeIntent } from "./fresh-solo";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";

/**
 * End-of-MATCH card. Reused by the online container.
 *
 * ⚠️ THREE OUTCOMES, NOT TWO. دوز plays a fixed number of دست, so a match can finish LEVEL — and
 * the engine says so with `ScoreResult.tied`, which is the only thing that distinguishes «both are
 * listed because they finished level» from «both are listed because they are partners». Reading
 * `winners.includes(you)` alone would announce «بردی!» to both players of a drawn match.
 */
export function DoozEndOverlay({
  view,
  result,
  self,
  opponent,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  view: DoozView;
  result: GameResult;
  /** card-98 item 12: the two players' rows — the same identities the board's strips get. */
  self?: SeatIdentity | null;
  opponent?: SeatIdentity | null;
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const tied = result.tied === true;
  const won = !tied && result.winners.includes(view.you);
  const title = tied ? fa.dooz.itsADraw : won ? fa.dooz.youWin : fa.dooz.youLose;
  /*
   * WHY IT ENDED — and it is TWO questions, not one. The card said «چهارتا رو ردیف کردی» for
   * every win there is, which is false in two reachable cases and was reported from an online
   * table: an opponent who walked out (the board is still «playing», so nothing on it explains
   * the result), and a match decided on دست‌ها after the LAST board filled up with no four.
   *
   * ① THE ROOM'S REASON WINS WHEN THE BOARD HAS NONE. `GameResult.reason` has ridden the
   *   `gameover` frame all along; at a table for two, «خروج بدون بازگشت» reaches
   *   `endForcedResult(twoPlayerWinScore(opponent), "resign")`. Same idiom as
   *   `ChessGame`/`BackgammonGame`, which already read it.
   *   ⚠️ AN IDLE REMOVAL NOW ENDS A دوز MATCH TOO, and it carries its OWN reason — `"idle"`
   *   (2026-09-21). At a PUBLIC table for two the removal forfeits exactly as «خروج» does, so
   *   this card has to tell the two apart: nobody left the table, and saying they did is false
   *   on BOTH screens — the removed player is still sitting there reading it. An EXPIRED DROP
   *   GRACE still does not end the match (the chair becomes a bot's and play continues) and
   *   still has no reason string; do not invent one for it.
   * ② OTHERWISE THE BOARD ANSWERS, and `handResult.winner === null` means the deciding دست was
   *   a full board: the match was won on دست‌ها, not by lining up four.
   */
  const roomReason =
    result.reason === "resign"
      ? won
        ? fa.dooz.endOpponentLeft
        : fa.dooz.endYouLeft
      : result.reason === "idle"
        ? won
          ? fa.dooz.endOpponentIdle
          : fa.dooz.endYouIdle
        : null;
  const drawnLastHand = view.handResult ? view.handResult.winner === null : false;
  const boardReason = tied
    ? fa.dooz.drawSubtitle
    : won
      ? drawnLastHand
        ? fa.dooz.winOnHands
        : fa.dooz.winSubtitle
      : drawnLastHand
        ? fa.dooz.loseOnHands
        : fa.dooz.loseSubtitle;
  const subtitle = roomReason ?? boardReason;
  // card-98 item 12: the shared template — the gold disc (this card's own), each PLAYER a side (as شلم
  // draws a team), the winner first, with دوز's own figure: the دست‌ها each one won
  const opp = view.you === 0 ? 1 : 0;
  const order = won || tied ? [view.you, opp] : [opp, view.you];
  return (
    <MatchEndCard
      label={fa.dooz.gameOver}
      testId="dooz-end"
      outcome={tied ? "drawn" : won ? "won" : "lost"}
      glyph="🔴"
      title={title}
      reason={subtitle}
      sides={order.map((seat) => ({
        key: `seat-${seat}`,
        mine: seat === view.you,
        members: [
          seat === view.you
            ? namedMember(seat, self, fa.dooz.you, "fox")
            : namedMember(seat, opponent, fa.online.bot, "wolf"),
        ],
        // a frame with no tally (an old server's, a partial test view) shows none rather than crash
        result: view.wins ? faDigits(view.wins[seat] ?? 0) : undefined,
        attrs: { "data-seat": seat },
      }))}
      playAgain={fa.dooz.playAgain}
      leave={fa.dooz.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** Pre-game START screen: colour, opponent level, and how many دست. All three LOCK for the match. */
function DoozStartScreen({
  color,
  onColorChange,
  difficulty,
  onDifficultyChange,
  hands,
  onHandsChange,
  onStart,
  onLeave,
}: {
  color: DoozPieceColor;
  onColorChange: (color: DoozPieceColor) => void;
  difficulty: BotDifficulty;
  onDifficultyChange: (value: BotDifficulty) => void;
  hands: DoozMatchLength;
  onHandsChange: (value: DoozMatchLength) => void;
  onStart: () => void;
  onLeave: () => void;
}): ReactNode {
  return (
    <StartScreenShell
      title={fa.dooz.title}
      emoji="🔴"
      heading={fa.dooz.chooseSettings}
      leaveLabel={fa.dooz.leave}
      startLabel={fa.dooz.startGame}
      onStart={onStart}
      onLeave={onLeave}
    >
      <StartRow label={fa.dooz.color}>
        <DoozColorSelect value={color} onChange={onColorChange} />
      </StartRow>
      {/* Three real tiers: they are three SEARCH DEPTHS with their own blunder rates, measured
          against each other before shipping (`apps/game-server/tools/dooz-probes/FINDINGS.md`). */}
      <StartRow label={fa.dooz.difficulty}>
        <DifficultySelect
          value={difficulty}
          onChange={onDifficultyChange}
          ariaLabel={fa.dooz.difficulty}
          levels={BOT_DIFFICULTIES}
          compact
        />
      </StartRow>
      <StartRow label={fa.dooz.hands}>
        <DoozHandsSelect value={hands} onChange={onHandsChange} />
      </StartRow>
    </StartScreenShell>
  );
}

/** Owns the solo دوز connection + store for ONE match at a FIXED difficulty and length. */
function DoozSoloRound({
  color,
  difficulty,
  hands,
  onAgain,
  onExit,
}: {
  color: DoozPieceColor;
  difficulty: BotDifficulty;
  hands: DoozMatchLength;
  onAgain: () => void;
  onExit: () => void;
}): ReactNode {
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  // Bumped by the channel gate's «عضو شدم» to re-attempt the join (a fresh store).
  const [round, setRound] = useState(0);
  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<DoozMove, DoozView>({
          wsUrl: GAME_SERVER_URL,
          room: "dooz",
          getInitData: () => initData,
          // FIXED for this match — chosen on the start screen. The server re-validates both
          // (`matchLengthRule` clamps the length to 3/5; `resolveDifficulty` the tier).
          difficulty,
          matchLength: hands,
        }),
      ),
    [initData, round, difficulty, hands],
  );
  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  const showEnd = useEndReveal(result !== null);
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      onExit();
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  const { loading, progress } = useSoloLoad(view != null, ready, store);

  const doLeave = (): void => {
    store.getState().disconnect();
    onExit();
  };
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.dooz.connectionError}
          </p>
          <Button variant="primary" onClick={onExit}>
            {fa.dooz.leave}
          </Button>
        </div>
      </div>
    );
  }
  // The connecting splash carries its own exit — never trap the player on it.
  if (loading || !view)
    return (
      <Boot
        caption={fa.dooz.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.dooz.leave}
      />
    );

  return (
    /* The solo human is SEAT 0, so their pick IS «the colour seat 0 plays in» — the one thing
       `DoozColorContext` carries. The bot takes the other; no second provider, no second rule. */
    <DoozColorContext.Provider value={color}>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <DoozTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : () => setConfirmingLeave(true)}
        self={profile}
        opponent={null}
      />
      {result && showEnd ? (
        <DoozEndOverlay
          result={result}
          view={view}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            clearActiveGame("dooz");
            void store
              .getState()
              .endGame()
              .catch(() => {})
              .finally(doLeave);
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
    </DoozColorContext.Provider>
  );
}

/** Solo دوز: START screen (locked choices) → the match. */
export function DoozGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  // ⚠️ EVERY SOLO GAME MUST CONSUME `takeResumeIntent()`, including one that would rather not:
  // the lobby arms the flag for EVERY solo resume, so a game that never reads it leaves it set
  // and the NEXT game that does read it silently skips its own picker, once, invisibly.
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [color, setColor] = useState<DoozPieceColor>(getDoozColor());
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getDoozDifficulty());
  const [hands, setHands] = useState<DoozMatchLength>(getDoozHands());

  if (phase === "select") {
    return (
      <DoozStartScreen
        color={color}
        onColorChange={(c) => {
          setColor(c);
          setDoozColor(c);
        }}
        difficulty={difficulty}
        onDifficultyChange={(value) => {
          setDifficulty(value);
          setDoozDifficulty(value);
        }}
        hands={hands}
        onHandsChange={(value) => {
          setHands(value);
          setDoozHands(value);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  return (
    <DoozSoloRound
      color={color}
      difficulty={difficulty}
      hands={hands}
      onAgain={() => setPhase("select")}
      onExit={() => setView("lobby")}
    />
  );
}
